import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { contentImageProps, heroImageProps } from '../utils/imageDelivery';
import { writeResearchConsentSupplement } from '../services/lmsData';
import './Pages/Dashboard.css';
import './ElearningEnrollmentModal.css';

/**
 * When false: after “I consent and continue”, enrollment skips Step 2 (questionnaire UI stays in code below).
 * Set to true to show the full consent supplement form again.
 */
const SHOW_ENROLLMENT_RESEARCH_QUESTIONNAIRE = false;

const RESEARCH_CONSENT_FORM_PATH = '/enrollment/CFREB_Consent_story_based_reflection.docx';
const RESEARCH_CONSENT_FORM_FILENAME = 'CFREB_Consent_story_based_reflection.docx';

const initialResearchConsentForm = () => ({
  audioTaped: '',
  videoTaped: '',
  remainAnonymous: '',
  anonymousWithPseudonym: '',
  pseudonym: '',
  quoteWithName: '',
  receiveSummary: '',
  summaryContact: '',
  followUpInterview: '',
  participantPrintedName: '',
  participantDate: new Date().toISOString().slice(0, 10),
});

function ResearchConsentYesNoRow({ fieldKey, label, error, value, onChange, questionNumber }) {
  const legendId = `${fieldKey}-legend`;
  return (
    <div className="elearning-enrollment-consent-row" role="group" aria-labelledby={legendId}>
      <div className="elearning-enrollment-consent-row__main">
        {questionNumber != null ? (
          <span className="elearning-enrollment-consent-row__num" aria-hidden="true">
            {questionNumber}.
          </span>
        ) : null}
        <p className="elearning-enrollment-consent-row__label" id={legendId}>
          {label}
        </p>
      </div>
      <div className="elearning-enrollment-consent-row__choices">
        <button
          type="button"
          className={`elearning-enrollment-consent-mini ${value === 'yes' ? 'elearning-enrollment-consent-mini--yes' : ''}`}
          aria-pressed={value === 'yes'}
          onClick={() => onChange('yes')}
        >
          Yes
        </button>
        <button
          type="button"
          className={`elearning-enrollment-consent-mini ${value === 'no' ? 'elearning-enrollment-consent-mini--no' : ''}`}
          aria-pressed={value === 'no'}
          onClick={() => onChange('no')}
        >
          No
        </button>
      </div>
      {error ? (
        <p className="elearning-enrollment-consent-row__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Same card as Learning Hub: image on top, title, E-learning, Start. */
export default function ElearningCourseCard({ module, imagePriority, workplaceConsentSignal = 0 }) {
  const navigate = useNavigate();
  const isComingSoon = Boolean(module.comingSoon);
  const isLocked = module.status === 'locked';
  const hasProgress = Number(module.progress || 0) > 0;
  const status = module.status || 'not-started';
  const isModuleCompleted = status === 'completed' || Boolean(module.isCompleted);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [enrollmentModalPhase, setEnrollmentModalPhase] = useState('consent'); // 'consent' | 'research-form'
  const [researchForm, setResearchForm] = useState(initialResearchConsentForm);
  const [researchFormErrors, setResearchFormErrors] = useState({});
  const [researchSubmitting, setResearchSubmitting] = useState(false);
  const prettifyStage = (value) =>
    String(value || '')
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  const lastStageLabel = module.currentStageId
    ? prettifyStage(module.currentStageId)
    : null;
  const linkTo = module.isStudyModule
    ? `/study-module/${module.id}`
    : `/modules/${module.id}`;
  const imageUrl = module.imageUrl || null;
  const isWorkplaceStartFlow = module.id === 'workplace-module' && status === 'not-started';

  const resetEnrollmentModal = useCallback(() => {
    setEnrollmentModalPhase('consent');
    setResearchForm(initialResearchConsentForm());
    setResearchFormErrors({});
    setResearchSubmitting(false);
  }, []);

  const openEnrollmentModal = useCallback(() => {
    resetEnrollmentModal();
    setConsentModalOpen(true);
  }, [resetEnrollmentModal]);

  const workplaceConsentHandledRef = useRef(0);
  useEffect(() => {
    if (module.id !== 'workplace-module') return;
    if (!workplaceConsentSignal || workplaceConsentSignal === workplaceConsentHandledRef.current) return;
    workplaceConsentHandledRef.current = workplaceConsentSignal;
    if (status !== 'not-started') return;
    openEnrollmentModal();
  }, [workplaceConsentSignal, module.id, status, openEnrollmentModal]);

  const closeEnrollmentModal = () => {
    setConsentModalOpen(false);
    resetEnrollmentModal();
  };

  const handleConsentNo = () => {
    closeEnrollmentModal();
    navigate(linkTo);
  };

  const handleConsentYes = async () => {
    if (SHOW_ENROLLMENT_RESEARCH_QUESTIONNAIRE) {
      setEnrollmentModalPhase('research-form');
      return;
    }
    if (researchSubmitting) return;
    setResearchSubmitting(true);
    const result = await writeResearchConsentSupplement({
      moduleId: module.id,
      consentOnly: true,
    });
    setResearchSubmitting(false);
    if (!result.ok) {
      window.alert('We could not save your consent. Please check your connection and try again.');
      return;
    }
    closeEnrollmentModal();
    navigate(linkTo);
  };

  const setResearchField = (key, value) => {
    setResearchForm((prev) => ({ ...prev, [key]: value }));
    setResearchFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateResearchForm = () => {
    const f = researchForm;
    const err = {};
    const need = (k, msg) => {
      if (!f[k]) err[k] = msg;
    };
    need('audioTaped', 'Please select Yes or No.');
    need('videoTaped', 'Please select Yes or No.');
    need('remainAnonymous', 'Please select Yes or No.');
    need('anonymousWithPseudonym', 'Please select Yes or No.');
    if (f.anonymousWithPseudonym === 'yes' && !String(f.pseudonym || '').trim()) {
      err.pseudonym = 'Please enter a pseudonym.';
    }
    need('quoteWithName', 'Please select Yes or No.');
    need('receiveSummary', 'Please select Yes or No.');
    if (f.receiveSummary === 'yes' && !String(f.summaryContact || '').trim()) {
      err.summaryContact = 'Please provide an email address or phone number.';
    }
    need('followUpInterview', 'Please select Yes or No.');
    if (!String(f.participantDate || '').trim()) {
      err.participantDate = 'Please enter the date.';
    }
    setResearchFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleResearchFormSubmit = async () => {
    if (!validateResearchForm() || researchSubmitting) return;
    setResearchSubmitting(true);
    const payload = {
      moduleId: module.id,
      audioTapedPermission: researchForm.audioTaped,
      videoTapedPermission: researchForm.videoTaped,
      wishRemainAnonymous: researchForm.remainAnonymous,
      wishAnonymousWithPseudonym: researchForm.anonymousWithPseudonym,
      pseudonym: researchForm.anonymousWithPseudonym === 'yes' ? String(researchForm.pseudonym || '').trim() : null,
      quoteWithNamePermission: researchForm.quoteWithName,
      receiveStudySummary: researchForm.receiveSummary,
      summaryContactInfo:
        researchForm.receiveSummary === 'yes' ? String(researchForm.summaryContact || '').trim() : null,
      followUpInterviewInterest: researchForm.followUpInterview,
      participantPrintedName: String(researchForm.participantPrintedName || '').trim() || null,
      participantDate: String(researchForm.participantDate || '').trim(),
    };
    const result = await writeResearchConsentSupplement(payload);
    if (!result.ok) {
      window.alert('We could not save your responses. Please check your connection and try again.');
      setResearchSubmitting(false);
      return;
    }
    setResearchSubmitting(false);
    closeEnrollmentModal();
    navigate(linkTo);
  };

  return (
    <div
      className={`elearning-course-card ${isLocked ? 'locked' : ''} ${isComingSoon ? 'elearning-course-card--coming-soon' : ''} elearning-course-card--${module.color || 'purple'}`}
    >
      <div className="elearning-course-card-image">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="elearning-course-card-img"
            {...(imagePriority ? heroImageProps : contentImageProps())}
          />
        ) : (
          <div className="elearning-course-card-image-placeholder">
            <span className="elearning-course-card-image-icon" aria-hidden="true">{module.icon}</span>
            <span className="elearning-course-card-image-title">{module.title}</span>
          </div>
        )}
      </div>
      <div className="elearning-course-card-details">
        <div className="elearning-course-heading-row">
          <h3 className="elearning-course-title">
            {module.title}
            {module.isNew && <span className="elearning-course-new">New</span>}
          </h3>
          {!isLocked && !isComingSoon && status !== 'not-started' && (
            <span className={`elearning-course-status ${status === 'completed' ? 'completed' : 'enrolled'}`}>
              {status === 'completed' ? 'COMPLETED' : status === 'in-progress' ? 'IN PROGRESS' : 'ENROLLED'}
            </span>
          )}
        </div>
        {/* <div className="elearning-course-meta">
          <span className="elearning-course-type">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            E-learning
          </span>
        </div> */}
        <div className={`elearning-course-footer-row ${isComingSoon ? 'elearning-course-footer-row--coming-soon' : ''}`}>
          {isComingSoon && (
            <div className="elearning-course-coming-soon-footer">
              <button
                type="button"
                className="elearning-course-link elearning-course-link--disabled"
                disabled
                aria-disabled="true"
              >
                Start
              </button>
              <div className="elearning-course-coming-soon-meta" aria-live="polite">
                <span className="elearning-course-coming-soon-badge">Coming soon</span>
                <span className="elearning-course-release-label">{module.releaseLabel || 'Releasing Q2 2026'}</span>
              </div>
            </div>
          )}
          {!isLocked && !isComingSoon && (
            isWorkplaceStartFlow ? (
              <button
                type="button"
                className="elearning-course-link elearning-course-link--button"
                onClick={openEnrollmentModal}
              >
                Start
              </button>
            ) : (
              <Link to={linkTo} className="elearning-course-link">
                {status === 'completed' ? 'Review' : status === 'in-progress' ? 'Continue' : 'Start'}
              </Link>
            )
          )}
          <div className="elearning-course-progress elearning-course-progress--right">
            {!isComingSoon && hasProgress && !isModuleCompleted && (
              <span className="elearning-course-progress-pill">
                {Math.round(Number(module.progress || 0))}% complete
              </span>
            )}
            {!isComingSoon && lastStageLabel && !isModuleCompleted && (
              <span className="elearning-course-stage-pill">
                Stage: {lastStageLabel}
              </span>
            )}
          </div>
        </div>
        {isLocked && !isComingSoon && <span className="elearning-course-locked">Coming soon</span>}
      </div>

      {typeof document !== 'undefined' &&
        consentModalOpen &&
        createPortal(
          <div className="elearning-enrollment-overlay" role="dialog" aria-modal="true" aria-labelledby={`workplace-consent-title-${module.id}`}>
            <div
              className={`elearning-enrollment-modal ${enrollmentModalPhase === 'research-form' ? 'elearning-enrollment-modal--research' : 'elearning-enrollment-modal--consent'}`}
            >
              {enrollmentModalPhase === 'consent' ? (
                <>
                  <div className="elearning-enrollment-research-hero elearning-enrollment-research-hero--consent">
                    <div className="elearning-enrollment-research-hero__row">
                      <span className="elearning-enrollment-research-chip">Research participation</span>
                      <div className="elearning-enrollment-modal-header-end">
                        <span className="elearning-enrollment-research-step">
                          {/* {SHOW_ENROLLMENT_RESEARCH_QUESTIONNAIRE ? 'Step 1 of 2' : 'Consent'} */}
                        </span>
                        <button
                          type="button"
                          className="elearning-enrollment-modal-close"
                          aria-label="Close"
                          onClick={closeEnrollmentModal}
                          disabled={researchSubmitting}
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </div>
                    </div>
                    <h3 id={`workplace-consent-title-${module.id}`} className="elearning-enrollment-title elearning-enrollment-title--research">
                      Consent - Building Workplace Bridges
                    </h3>
                  </div>
                  <div className="elearning-enrollment-consent-copy">
                    <p>Welcome to the Building Workplace Bridges Module!</p>
                    <p>
                      This module is designed to support reflective leadership practice through stories informed by our Breaking Barriers project.
                    </p>
                    <p>
                      After completing the module, you will be asked to fill out a short survey after which you may download a certificate of completion. You can also select if you would like to participate in an optional one-on-one interview (to be scheduled after 3 months and per your convenience) to share more insights about your experience.
                    </p>
                    <p>
                      Please note: The survey responses will be anonymized and are designed to support your personal learning and reflection. They also help us at Lotus STEMM to evaluate the effectiveness of this learning.
                    </p>
                    <p>
                      In addition, you may choose to allow your responses to be used in a doctoral research study at the University of Calgary examining how story-based reflection supports inclusive leadership in STEMM workplaces.
                    </p>
                    <p>You may:</p>
                    <ul>
                      <li>Complete the module and survey without being part of the research, or</li>
                      <li>Consent to research participation and allow your anonymous responses to be used in the academic study.</li>
                    </ul>
                    <p>
                      Your participation is completely voluntary.
                      {SHOW_ENROLLMENT_RESEARCH_QUESTIONNAIRE
                        ? ' If you choose research participation, you will complete a short consent supplement on the next screen (in place of a separate document) before the module opens.'
                        : ' If you choose research participation, your consent will be saved and you can begin the module.'}
                    </p>
                    <p>
                      The University of Calgary Conjoint Faculties Research Ethics Board has approved this study (REB25-1873).
                    </p>
                    <p className="elearning-enrollment-investigator">
                      <strong>Primary Investigator:</strong><br />
                      Dr. Katherine Lawless<br />
                      Assistant Professor, Department of Communication, Media and Film<br />
                      University of Calgary<br />
                      <a href="mailto:katherine.lawless@ucalgary.ca">katherine.lawless@ucalgary.ca</a>
                    </p>
                    <p className="elearning-enrollment-note">
                      For more information, please refer to{' '}
                      <a
                        href={`${process.env.PUBLIC_URL || ''}${RESEARCH_CONSENT_FORM_PATH}`}
                        download={RESEARCH_CONSENT_FORM_FILENAME}
                        className="elearning-enrollment-note-link"
                      >
                        this consent form
                      </a>
                      .
                    </p>
                  </div>
                  <div className="elearning-enrollment-actions">
                    <button
                      type="button"
                      className="elearning-enrollment-btn elearning-enrollment-btn--ghost"
                      onClick={handleConsentNo}
                      disabled={researchSubmitting}
                    >
                      Continue without research
                    </button>
                    <button
                      type="button"
                      className="elearning-enrollment-btn elearning-enrollment-btn--primary"
                      onClick={() => void handleConsentYes()}
                      disabled={researchSubmitting}
                    >
                      {researchSubmitting && !SHOW_ENROLLMENT_RESEARCH_QUESTIONNAIRE ? 'Saving…' : 'I consent and continue'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="elearning-enrollment-research-hero">
                    <div className="elearning-enrollment-research-hero__row">
                      <span className="elearning-enrollment-research-chip">Consent supplement</span>
                      <div className="elearning-enrollment-modal-header-end">
                        <span className="elearning-enrollment-research-step">Step 2 of 2</span>
                        <button
                          type="button"
                          className="elearning-enrollment-modal-close"
                          aria-label="Close"
                          onClick={closeEnrollmentModal}
                          disabled={researchSubmitting}
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </div>
                    </div>
                    <h3 id={`workplace-consent-title-${module.id}`} className="elearning-enrollment-title elearning-enrollment-title--research">
                      Research consent - additional items
                    </h3>
                    <p className="elearning-enrollment-research-lead">
                      Answer each line; optional fields appear when relevant.
                    </p>
                  </div>
                  <div className="elearning-enrollment-form-scroll">
                    <div className="elearning-enrollment-consent-formsheet">
                      <ResearchConsentYesNoRow
                        fieldKey="audioTaped"
                        label="I grant permission to be audio-taped."
                        error={researchFormErrors.audioTaped}
                        value={researchForm.audioTaped}
                        onChange={(v) => setResearchField('audioTaped', v)}
                        questionNumber={1}
                      />
                      <ResearchConsentYesNoRow
                        fieldKey="videoTaped"
                        label="I grant permission to be video-taped."
                        error={researchFormErrors.videoTaped}
                        value={researchForm.videoTaped}
                        onChange={(v) => setResearchField('videoTaped', v)}
                        questionNumber={2}
                      />
                      <ResearchConsentYesNoRow
                        fieldKey="remainAnonymous"
                        label="I wish to remain anonymous."
                        error={researchFormErrors.remainAnonymous}
                        value={researchForm.remainAnonymous}
                        onChange={(v) => setResearchField('remainAnonymous', v)}
                        questionNumber={3}
                      />
                      <ResearchConsentYesNoRow
                        fieldKey="anonymousWithPseudonym"
                        label="I wish to remain anonymous, but you may refer to me by a pseudonym."
                        error={researchFormErrors.anonymousWithPseudonym}
                        value={researchForm.anonymousWithPseudonym}
                        onChange={(v) => setResearchField('anonymousWithPseudonym', v)}
                        questionNumber={4}
                      />
                      <div
                        className={`elearning-enrollment-consent-field ${researchForm.anonymousWithPseudonym !== 'yes' ? 'elearning-enrollment-consent-field--muted' : ''}`}
                      >
                        <label className="elearning-enrollment-consent-field__label" htmlFor="research-pseudonym">
                          Pseudonym (only if you answered Yes to using one):
                        </label>
                        <input
                          id="research-pseudonym"
                          type="text"
                          className="elearning-enrollment-text-input elearning-enrollment-text-input--compact"
                          value={researchForm.pseudonym}
                          onChange={(e) => setResearchField('pseudonym', e.target.value)}
                          autoComplete="off"
                          disabled={researchForm.anonymousWithPseudonym !== 'yes'}
                          placeholder="Enter a pseudonym"
                        />
                        {researchForm.anonymousWithPseudonym !== 'yes' ? (
                          <p className="elearning-enrollment-field-hint">Enable by answering Yes on item 4.</p>
                        ) : null}
                        {researchFormErrors.pseudonym ? (
                          <p className="elearning-enrollment-field-error" role="alert">
                            {researchFormErrors.pseudonym}
                          </p>
                        ) : null}
                      </div>
                      <ResearchConsentYesNoRow
                        fieldKey="quoteWithName"
                        label="You may quote me and use my name."
                        error={researchFormErrors.quoteWithName}
                        value={researchForm.quoteWithName}
                        onChange={(v) => setResearchField('quoteWithName', v)}
                        questionNumber={5}
                      />
                      <ResearchConsentYesNoRow
                        fieldKey="receiveSummary"
                        label="Would you like to receive a summary of the study’s results?"
                        error={researchFormErrors.receiveSummary}
                        value={researchForm.receiveSummary}
                        onChange={(v) => setResearchField('receiveSummary', v)}
                        questionNumber={6}
                      />
                      <div
                        className={`elearning-enrollment-consent-field ${researchForm.receiveSummary !== 'yes' ? 'elearning-enrollment-consent-field--muted' : ''}`}
                      >
                        <label className="elearning-enrollment-consent-field__label" htmlFor="research-summary-contact">
                          Contact for study summary (only if Yes above):
                        </label>
                        <textarea
                          id="research-summary-contact"
                          className="elearning-enrollment-textarea elearning-enrollment-textarea--compact"
                          rows={2}
                          value={researchForm.summaryContact}
                          onChange={(e) => setResearchField('summaryContact', e.target.value)}
                          disabled={researchForm.receiveSummary !== 'yes'}
                          placeholder="Email or phone"
                        />
                        {researchForm.receiveSummary !== 'yes' ? (
                          <p className="elearning-enrollment-field-hint">Enable by answering Yes on item 6.</p>
                        ) : null}
                        {researchFormErrors.summaryContact ? (
                          <p className="elearning-enrollment-field-error" role="alert">
                            {researchFormErrors.summaryContact}
                          </p>
                        ) : null}
                      </div>
                      <ResearchConsentYesNoRow
                        fieldKey="followUpInterview"
                        label="Are you interested in being contacted about a follow-up interview, with the understanding that you can always decline the request?"
                        error={researchFormErrors.followUpInterview}
                        value={researchForm.followUpInterview}
                        onChange={(v) => setResearchField('followUpInterview', v)}
                        questionNumber={7}
                      />
                      <div className="elearning-enrollment-consent-field elearning-enrollment-consent-field--signoff">
                        <div className="elearning-enrollment-signoff-grid">
                          <div className="elearning-enrollment-field elearning-enrollment-field--flush">
                            <label className="elearning-enrollment-consent-field__label" htmlFor="research-participant-name">
                              Printed name (optional)
                            </label>
                            <input
                              id="research-participant-name"
                              type="text"
                              className="elearning-enrollment-text-input elearning-enrollment-text-input--compact"
                              value={researchForm.participantPrintedName}
                              onChange={(e) => setResearchField('participantPrintedName', e.target.value)}
                              autoComplete="name"
                            />
                          </div>
                          <div className="elearning-enrollment-field elearning-enrollment-field--flush">
                            <label className="elearning-enrollment-consent-field__label" htmlFor="research-participant-date">
                              Date
                            </label>
                            <input
                              id="research-participant-date"
                              type="date"
                              className="elearning-enrollment-text-input elearning-enrollment-text-input--compact elearning-enrollment-text-input--date"
                              value={researchForm.participantDate}
                              onChange={(e) => setResearchField('participantDate', e.target.value)}
                            />
                            {researchFormErrors.participantDate ? (
                              <p className="elearning-enrollment-field-error" role="alert">
                                {researchFormErrors.participantDate}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="elearning-enrollment-footer">
                    <div className="elearning-enrollment-actions elearning-enrollment-actions--split">
                      <button
                        type="button"
                        className="elearning-enrollment-btn elearning-enrollment-btn--ghost elearning-enrollment-btn--lg"
                        onClick={() => setEnrollmentModalPhase('consent')}
                        disabled={researchSubmitting}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="elearning-enrollment-btn elearning-enrollment-btn--primary elearning-enrollment-btn--lg"
                        onClick={() => void handleResearchFormSubmit()}
                        disabled={researchSubmitting}
                      >
                        {researchSubmitting ? 'Saving…' : 'Save and start module'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}

    </div>
  );
}
